import * as VueRouter from 'vue-router'

import AppShell from './components/AppShell.vue'
import config from './config'
import { usePostHog } from './utils/posthog'

declare module 'vue-router' {
  interface RouteMeta {
    checkFeature?: () => boolean | undefined
  }
}

const { posthog } = usePostHog()

export const routes: VueRouter.RouteRecordRaw[] = [
  { path: '/index.html', redirect: '/' },
  {
    path: '/auth/welcome',
    component: () => import('./pages/auth/AuthWelcomePage.vue'),
  },
  {
    path: '/auth/error',
    component: () => import('./pages/auth/AuthErrorPage.vue'),
  },
  {
    path: '/',
    component: AppShell,
    children: [
      {
        path: '',
        component: () => import('./pages/home/Home.vue'),
      },
      {
        path: 'workflows/:workflowId',
        component: () => import('./pages/workflows/_id/Viewer.vue'),
      },
      {
        path: '/editor/:project?',
        component: () => import('./pages/editor/CompoundEditor.vue'),
      },
      {
        path: '/create',
        component: () => import('./pages/TemplateGallery.vue'),
      },
      {
        path: '/create/compound',
        component: () => import('./components/CreateCompoundsDialog.vue'),
      },
      {
        path: '/create/preparation',
        children: [
          {
            path: 'prepare-liquid',
            component: () => import('./pages/create/preparation/prepare-liquid/PrepareLiquid.vue'),
          },
          {
            path: 'prepare-solid-liquid',
            component: () =>
              import('./pages/create/preparation/prepare-liquid-solid/PrepareLiquidSolid.vue'),
          },
          {
            path: 'prepare-random-alloy',
            component: () =>
              import('./pages/create/preparation/prepare-random-alloy/PrepareRandomAlloy.vue'),
          },
          {
            path: 'prepare-solvation-shell',
            component: () =>
              import('./pages/create/preparation/prepare-clusters/PrepareClusters.vue'),
          },
          {
            path: 'prepare-crystal',
            component: () =>
              import('./pages/create/preparation/prepare-crystal/PrepareCrystal.vue'),
          },
          {
            path: 'prepare-update-crystal-composition',
            component: () =>
              import('./pages/create/preparation/prepare-update-crystal-composition/PrepareUpdateCrystalComposition.vue'),
          },
          {
            path: 'prepare-crystal-defects',
            component: () =>
              import('./pages/create/preparation/prepare-crystal-defects/PrepareCrystalDefects.vue'),
          },
          {
            path: 'prepare-surface',
            component: () =>
              import('./pages/create/preparation/prepare-surface/PrepareSurface.vue'),
          },
          {
            path: 'prepare-surface-adsorbate',
            component: () =>
              import('./pages/create/preparation/prepare-surface-adsorbate/PrepareSurfaceAdsorbate.vue'),
          },
          {
            path: 'prepare-grain-boundary',
            component: () =>
              import('./pages/create/preparation/prepare-grain-boundary/PrepareGrainBoundary.vue'),
          },
          {
            path: 'prepare-force-field',
            component: () =>
              import('./pages/create/preparation/prepare-force-field/PrepareForceField.vue'),
          },
        ],
      },
      {
        path: '/create/simulation',
        children: [
          {
            path: 'viscosity',
            component: () =>
              import('./pages/create/simulation/viscosity/CreateViscositySimulation.vue'),
          },
          {
            path: 'spectral-analysis',
            component: () =>
              import('./pages/create/simulation/spectral-analysis/CreateSpectralAnalysisSimulation.vue'),
          },
          {
            path: 'bandstructure',
            component: () =>
              import('./pages/create/simulation/bandstructure/CreateBandstructureSimulation.vue'),
          },
          {
            path: 'surface-adsorption',
            component: () =>
              import('./pages/create/simulation/surface-adsorption/CreateSurfaceAdsorptionSimulation.vue'),
          },
          {
            path: 'surface-adsorption-ml',
            component: () =>
              import('./pages/create/simulation/surface-adsorption-ml/CreateSurfaceAdsorptionMlSimulation.vue'),
          },
          {
            path: 'density',
            component: () =>
              import('./pages/create/simulation/density/CreateDensitySimulation.vue'),
          },
          {
            path: 'dos',
            component: () => import('./pages/create/simulation/dos/CreateDOSSimulation.vue'),
          },
          {
            path: 'diffusion',
            component: () =>
              import('./pages/create/simulation/diffusion/CreateDiffusionSimulation.vue'),
          },
          {
            path: 'ionic-conductivity',
            component: () =>
              import('./pages/create/simulation/ionic-conductivity/CreateIonicConductivitySimulation.vue'),
          },
          {
            path: 'glass-temperature',
            component: () =>
              import('./pages/create/simulation/glass-temperature/CreateGlassTemperatureSimulation.vue'),
          },
          {
            path: 'mechanical-properties',
            component: () =>
              import('./pages/create/simulation/mechanical-properties/CreateMechanicalPropertiesSimulation.vue'),
          },
          {
            path: 'nanoreactor',
            component: () =>
              import('./pages/create/simulation/nanoreactor/CreateNanoreactorSimulation.vue'),
          },
          {
            path: 'ocv',
            component: () => import('./pages/create/simulation/ocv/CreateOCVSimulation.vue'),
          },
          {
            path: 'ocv-ml',
            component: () => import('./pages/create/simulation/ocv-ml/CreateOcvMlSimulation.vue'),
          },
          {
            path: 'optical-spectrum',
            component: () =>
              import('./pages/create/simulation/optical-spectrum/CreateOpticalSpectrumSimulation.vue'),
          },
          {
            path: 'optical-spectrum-analyzer',
            component: () =>
              import('./pages/create/simulation/optical-spectrum-analyzer/CreateOSASimulation.vue'),
          },
          {
            path: 'calculate-aimd',
            component: () =>
              import('./pages/create/simulation/calculate-aimd/CreateAIMDSimulation.vue'),
          },
          {
            path: 'spectral-analysis-ml',
            component: () =>
              import('./pages/create/simulation/spectral-analysis-ml/CreateSpectralAnalysisMLSimulation.vue'),
          },
          {
            path: 'electrochemical-stability-window',
            component: () =>
              import('./pages/create/simulation/electrochemical-stability-window/CreateElectrochemicalWindow.vue'),
          },
          {
            path: 'segregation',
            component: () =>
              import('./pages/create/simulation/segregation-energies/CreateSegregationEnergiesSimulation.vue'),
          },
          {
            path: 'segregation-energies-ml',
            component: () =>
              import('./pages/create/simulation/segregation-energies-ml/CreateSegregationEnergiesMlSimulation.vue'),
          },
          {
            path: 'snemeis',
            component: () =>
              import('./pages/create/simulation/snemeis/CreateSnemeisSimulation.vue'),
          },
          {
            path: 'mep',
            component: () => import('./pages/create/simulation/mep/CreateMepSimulation.vue'),
          },
          {
            path: 'mep-ml',
            component: () => import('./pages/create/simulation/mep-ml/CreateMepMlSimulation.vue'),
          },
          {
            path: 'liquid-absorption',
            component: () =>
              import('./pages/create/simulation/liquid-absorption/CreateLiquidAbsorptionSimulation.vue'),
          },
          {
            path: 'phonon-ml',
            component: () =>
              import('./pages/create/simulation/phonon-ml/CreatePhononMlSimulation.vue'),
          },
          {
            path: 'elastic-tensor-ml',
            component: () =>
              import('./pages/create/simulation/elastic-tensor-ml/CreateElasticTensorMlSimulation.vue'),
          },
          {
            path: 'structural-analysis',
            component: () =>
              import('./pages/create/simulation/structural-analysis/CreateStructuralAnalysisSimulation.vue'),
          },
          {
            path: 'xrd',
            component: () => import('./pages/create/simulation/xrd/CreateXrdSimulation.vue'),
          },
          {
            path: 'friction-coefficient',
            component: () =>
              import('./pages/create/simulation/friction-coefficient/CreateFrictionCoefficientSimulation.vue'),
          },
          {
            path: 'spectra-nmr-ml',
            component: () =>
              import('./pages/create/simulation/spectra-nmr-ml/CreateSpectraNmrMlSimulation.vue'),
          },
          {
            path: 'composition-stability',
            component: () =>
              import('./pages/create/simulation/composition-stability/CreateCompositionStabilitySimulation.vue'),
          },
          {
            path: 'solvation-free-energy',
            component: () =>
              import('./pages/create/simulation/solvation-free-energy/CreateSolvationFreeEnergySimulation.vue'),
          },
          {
            path: 'adhesion-enthalpy',
            component: () =>
              import('./pages/create/simulation/adhesion-enthalpy/CreateAdhesionEnthalpySimulation.vue'),
          },
          {
            path: 'reaction-rate',
            component: () =>
              import('./pages/create/simulation/reaction-rate/CreateReactionRateSimulation.vue'),
          },
          {
            path: 'bell-state',
            component: () =>
              import('./pages/create/simulation/bell-state/CreateBellStateSimulation.vue'),
          },
          {
            path: 'ground-state-energy',
            component: () =>
              import('./pages/create/simulation/ground-state-energy/CreateGroundStateEnergySimulation.vue'),
          },
        ],
      },
      {
        path: 'compounds/:compoundId',
        component: () => import('./pages/compounds/CompoundViewerPage.vue'),
      },
      {
        path: 'compounds/new',
        component: () => import('./pages/compounds/ExternalCompoundViewer.vue'),
      },
      {
        path: 'preparations/:preparationId',
        component: () => import('./pages/preparations/Viewer.vue'),
      },
      {
        path: '/:affiliationId',
        component: () => import('./pages/AffiliationDashboard.vue'),
      },
      {
        path: '/:affiliationId/:projectId',
        component: () => import('./pages/projects/ProjectDashboard.vue'),
        children: [
          {
            name: 'workflows',
            path: '',
            alias: 'workflows',
            component: () => import('./pages/projects/WorkflowView.vue'),
          },
          {
            name: 'compounds',
            path: 'compounds',
            component: () => import('./pages/projects/CompoundView.vue'),
          },
        ],
      },
      {
        path: '/account',
        component: () => import('./pages/account/Account.vue'),
      },
    ],
  },
]

routes.push({
  path: '/:pathMatch(.*)*',
  name: 'not-found',
  component: () => import('./pages/NotFoundError.vue'),
})

function createRouter() {
  return VueRouter.createRouter({
    history: VueRouter.createWebHistory(config.baseUrl),
    routes,
  })
}

export const router = createRouter()

router.afterEach((to) => {
  posthog.capture('$pageview', {
    route: to.matched.at(-1)?.path,
  })
})

router.beforeEach((to, from, next) => {
  if (from.path !== to.path) {
    posthog.capture('$pageleave')
  }

  if (to.meta.checkFeature) {
    if (to.meta.checkFeature()) {
      next()
    } else {
      next({ name: 'not-found' })
    }
  } else {
    next()
  }
})
